import { cloneGridDraft, decodeGridState, defaultGridView } from 'src/app/_helpers/grid-state';
import { HelperService } from 'src/app/_services/helper.service';
import { Component, ElementRef, OnInit, ViewChild, inject } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import * as _ from "lodash";
import { ToastrService } from "ngx-toastr";
import { DatatableData } from "src/app/_models/datatable-data";
import { GridStatesService } from "src/app/_services/grid-states.service";
import { ViewMode } from "../../products/product_filter";
import { DatatableComponent } from "../datatable/datatable.component";
import { EditGridStateComponent } from "../edit-grid-state/edit-grid-state.component";
import { FilterOption } from "../filters/filters.component";
import { ProgressHelper } from "../progress-helper/progress-helper";
import { SupplierDto, SuppliersService } from "src/app/_services/settings/suppliers.service";
import { take } from "rxjs";
import { Location } from "@angular/common";
declare var $: any;

export interface SearchOption {
  value: string,
  text: string,
  isDefault?: boolean;
}

@Component({ template: '' })
export class GridPageComponent extends ProgressHelper implements OnInit {

  private filterScopeHelper: HelperService = inject(HelperService);
  private get gridHistoryState(): any {
    return ['Inventory', 'Listings', 'Kits'].includes(this.gridId)
      ? this.filterScopeHelper.filterHistoryState : history.state;
  }
  private readonly saveRevisions = new WeakMap<object, number>();
  gridId: string;
  gridData: DatatableData = new DatatableData();
  allFilters: any = {};
  selectedStores: number[] = [];
  selectedLocations: number[] = [];
  selectedTags: number[] = [];
  selectedExcludedTags: number[] = [];
  gridStates: any[] = [];
  currentGridState: any;
  defaultGridState: any;
  disabledOptions: any = {};
  viewModes: ViewMode[];
  currentViewMode: ViewMode;
  filterOptions: FilterOption[];
  selectedFilters: FilterOption[] = [];
  loading = true;
  searchOptions: SearchOption[];
  searchBy: SearchOption;
  locationService: Location = inject(Location);
  translate: TranslateService = inject(TranslateService);
  toastr: ToastrService = inject(ToastrService);
  modalService: NgbModal = inject(NgbModal);
  gridStatesService: GridStatesService = inject(GridStatesService);
  suppliersService: SuppliersService = inject(SuppliersService);

  /**
   * When true, the component will keep browser history state
   * so navigation back to the page restores grid settings.
   */
  preserveHistoryState = false;

  @ViewChild('searchBox') searchBox: ElementRef;
  @ViewChild(DatatableComponent) datatable: DatatableComponent;

  constructor() {
    super();
  }

  ngOnInit() {
    this.searchOptions = this.searchOptions || [];
    this.searchBy = this.searchOptions.find(op => op.isDefault) || _.first(this.searchOptions);
    if (this.suppliersService && this.filterOptions)
      this.loadSupplierOptionsForAdvancedFilter();
    setTimeout(() => {
      this.defaultGridState = { columns: this.datatable.getGridColumns() };
      if (this.gridId) {
        this.gridStatesService.getAll(this.gridId).subscribe(result => {
          this.defaultGridState.gridId = this.gridId;
          if (result && result.length > 0) {
            this.gridStates = result.map(gs => {
              gs.gridColumns = decodeGridState(gs.gridColumns);
              if (gs.gridColumns.allFilters)
                this.allFilters = gs.gridColumns.allFilters;
              return gs;
            });
            let selectedGridState = defaultGridView(this.gridStates, this.gridHistoryState.gridState ?? this.gridStatesService.selectedViewId(this.gridId));
            this.setGridState(selectedGridState || this.gridStates[0], !this.gridHistoryState.gridData);
          } else {
            this.resetGridState(!this.gridHistoryState.gridData);
          }
          // remove cache from history state after using once unless
          // a child component wants to preserve it
          if (!this.preserveHistoryState) {
            this.locationService.replaceState(this.locationService.path(), '', {});
          }
        }, () => { this.loading = false; });
      } else {
        this.resetGridState(!this.gridHistoryState.gridData);
      }
    }, 0);
  }

  showColumnsChooser() {
    let gridState = {
      ...cloneGridDraft(this.currentGridState),
      columns: this.datatable.getGridColumns().map(col => { return { ...col }; })
    };
    let modal = this.modalService.open(EditGridStateComponent, { size: 'xl' as any, windowClass: 'right', scrollable: true, beforeDismiss: () => !modal.componentInstance.saving });
    modal.componentInstance.disabled = this.disabledOptions;
    modal.componentInstance.datatable = this.datatable;
    modal.componentInstance.gridState = gridState;
    modal.componentInstance.viewByOptions = this.viewModes;
    modal.componentInstance.filterOptions = this.filterOptions;
    modal.componentInstance.savedFilters = this.allFilters;
    modal.componentInstance.loading.subscribe(l => this.loading = l);
    modal.componentInstance.saved.subscribe(gridState => {
      if (!this.gridStates[0]?.id)
        this.gridStates = [];
      let gState = this.gridStates.find(gs => gs.id == gridState.id);
      if (gState) {
        gState.name = gridState.name;
        if (gridState.columns)
          gState.columns = gridState.columns;
        if (gridState.isDefault && !gState.isDefault) {
          let oldDefault = this.gridStates.find(gs => gs.id != gState.id && gs.isDefault);
          if (oldDefault) oldDefault.isDefault = false;
        }
        gState.isDefault = gridState.isDefault;
        this.gridStates[this.gridStates.findIndex(gs => gs.id == gridState.id)] = gridState;
      }
      else
        this.gridStates.push(gridState);
      if (gridState.isDefault) this.gridStates.forEach(view => view.isDefault = view.id === gridState.id);
      this.setGridState(gridState, true, true);
    });
    modal.componentInstance.deleted.subscribe(gridState => {
      this.gridStates.splice(this.gridStates.findIndex(gs => gs.id == gridState.id), 1);
      if (!this.gridStates.length)
        this.resetGridState();
      else
        this.setGridState(defaultGridView(this.gridStates), true, true);
    });
    modal.result.then(() => {}, () => {});
  }

  addNewTab() {
    let modal = this.modalService.open(EditGridStateComponent, { size: 'xl' as any, windowClass: 'right', scrollable: true, beforeDismiss: () => !modal.componentInstance.saving });
    modal.componentInstance.disabled = this.disabledOptions;
    modal.componentInstance.datatable = this.datatable;
    modal.componentInstance.gridId = this.gridId;
    let columns = cloneGridDraft(this.defaultGridState.columns).map(gCol => {
      gCol.visible = !gCol.freezed ? false : gCol.visible;
      return gCol;
    });
    modal.componentInstance.gridState = {
      name: '', gridId: this.gridId, columns: columns,
      gridColumns: {
        columns: columns, filters: {}, locations: [], stores: [], tags: [], excludedTags: [],
        viewBy: this.viewModes && this.viewModes.length ? this.viewModes[0].id : null
      }
    };
    modal.componentInstance.viewByOptions = this.viewModes;
    modal.componentInstance.filterOptions = this.filterOptions;
    modal.componentInstance.savedFilters = this.allFilters;
    modal.componentInstance.loading.subscribe(l => this.loading = l);
    modal.componentInstance.saved.subscribe(gridState => {
      if (!this.gridStates[0]?.id)
        this.gridStates = [];
      this.gridStates.push(gridState);
      if (gridState.isDefault) this.gridStates.forEach(view => view.isDefault = view.id === gridState.id);
      this.setGridState(gridState, true, true);
    });
    modal.result.then(() => {}, () => {});
  }

  resetGridState(reload: boolean = true) {
    let columns = this.datatable.getGridColumns(!!this.gridId);
    this.gridStates = [{
      ...this.defaultGridState, gridColumns: {
        columns: columns,
        filters: {}, locations: [], stores: [], tags: [], excludedTags: []
      }, columns: columns, isDefault: true
    }];
    this.setGridState(this.gridStates[0], reload);
    if (this.gridId)
      this.saveGridState();
  }

  setGridState(gridState: any, reload: boolean = true, switchingGrid = false) {
    gridState.gridColumns = decodeGridState(gridState.gridColumns);
    this.currentGridState = gridState;
    this.gridStatesService.rememberView(this.gridId, gridState.id);
    this.allFilters = _.cloneDeep(gridState.gridColumns.allFilters || {});
    this.searchBy = this.searchOptions.find(option => option.value === gridState.gridColumns.searchBy)
      || this.searchOptions.find(option => option.isDefault) || this.searchOptions[0];
    if (switchingGrid) {
      this.gridData.page = 1;
      this.gridData.selectedItems = [];
      if (this.searchBox?.nativeElement) this.searchBox.nativeElement.value = '';
    }

    if (reload) {
      this.gridData.columns = cloneGridDraft(gridState.gridColumns.columns);
      // Ensure the datatable immediately reflects saved visibility/order on reload
      if (this.datatable && this.gridData.columns) {
        this.datatable.gridColumns = this.gridData.columns;
      }
      this.gridData.filters = _.cloneDeep(gridState.gridColumns.filters || {});
      this.selectedFilters = [];
      if (this.gridData.filters) {
        for (const key of Object.keys(this.gridData.filters)) {
          const option = (this.filterOptions || []).find(f => f.field === key);
          // Search/status constraints are not editable advanced-filter definitions.
          if (option) this.selectedFilters.push({ ..._.cloneDeep(option), ...this.gridData.filters[key] });
        }

        if (this.searchBox?.nativeElement?.value?.trim() && this.searchBy?.value) {
          this.gridData.filters[this.searchBy.value] = { type: 'Contains', value: this.searchBox.nativeElement.value.trim() };
        }
      }
      this.gridData.sortField = gridState.gridColumns.sortBy;
      this.gridData.sort = gridState.gridColumns.sort;
      this.selectedLocations = _.cloneDeep(gridState.gridColumns.locations || []);
      this.selectedStores = _.cloneDeep(gridState.gridColumns.storesEntityMap || []);
      this.selectedTags = _.cloneDeep(gridState.gridColumns.tags || []);
      this.selectedExcludedTags = _.cloneDeep(gridState.gridColumns.excludedTags || []);
      if (this.viewModes && this.viewModes.length && gridState.gridColumns.viewBy)
        this.currentViewMode = this.viewModes.find(m => m.id == gridState.gridColumns.viewBy);


      if ((["Inventory", "Listings", "Kits"].includes(this.gridId)
        && (!gridState.gridColumns.columns))
        || (this.gridId == "Listings" && this.datatable.getGridColumns().some(gc => (gc.columns || []).includes('replenish1'))
          && !gridState.gridColumns.columns.some(c => (c.columns || []).includes('replenish1')))) {
        this.datatable.gridColumns = this.gridData.columns;
        gridState.gridColumns.columns = this.datatable.getGridColumns();
        this.gridData.columns = cloneGridDraft(gridState.gridColumns.columns);
        this.saveGridState(false);
      }
      if (switchingGrid) {
        this.gridData.page = 1;
      }
      setTimeout(() => $('.selectpicker').selectpicker('refresh'), 1);
      // Defer loadData if child wants to ensure grid-states readiness
      const selfAny: any = this as any;
      if (!selfAny.gridStatesReady && typeof selfAny.queuedLoad !== 'undefined') {
        // child will trigger load when ready
      } else {
        this.loadData();
      }
    }
    setTimeout(() => this.datatable.updateWidth());
  }

  selectSearchBy(option: SearchOption, reload: boolean = true) {
    const selected = option && this.searchOptions.find(op => op.value === option.value);

    if (this.searchOptions.length && selected) {
      this.searchBy = selected;
    }

    if (this.searchBox?.nativeElement?.value) {
      this.search({ target: this.searchBox.nativeElement, skip: !reload });
    }
  }

  search(e: any) {
    // Rebuild filters object without transient 'Contains' search entries to avoid delete on frozen objects
    if (this.gridData.filters && typeof this.gridData.filters === 'object') {
      const cleaned: any = {};
      for (const key of Object.keys(this.gridData.filters)) {
        const val: any = (this.gridData.filters as any)[key];
        if (!val || val.type !== 'Contains') cleaned[key] = val;
      }
      this.gridData.filters = cleaned;
    } else {
      this.gridData.filters = {};
    }
    this.gridData.page = 1;
    if (e.target.value && this.searchBy) {
      this.gridData.filters[this.searchBy.value] = { type: 'Contains', value: e.target.value.trim() };
      this.gridData.page = 1;
      this.setSearchBy(this.searchBy.value);
    }
    if (!e.skip)
      this.loadData();
  }

  get showDropdown() {
    return (document.getElementsByClassName('kt-portlet__head--lg')[0] as HTMLBRElement).offsetWidth < 1300;
  }

  /** Named filter presets are explicit preferences, separate from the live query. */
  saveFilters() {
    if (!this.currentGridState) return;
    this.currentGridState.gridColumns.allFilters = _.cloneDeep(this.allFilters);
    this.saveGridState(false);
  }

  setSearchBy(searchBy: string) {
    if (this.currentGridState.gridColumns.searchBy != searchBy) {
      this.currentGridState.gridColumns.searchBy = searchBy;
      if (this.gridId)
        this.saveGridState(false);
    }
  }

  saveGridState(notify: boolean = true) {
    const view = this.currentGridState;
    if (!view) return;
    const revision = (this.saveRevisions.get(view) || 0) + 1;
    this.saveRevisions.set(view, revision);
    this.gridStatesService.set(view, this.gridData.columns).subscribe({
      next: result => {
        view.id = result.id;
        if (this.saveRevisions.get(view) !== revision) return;
        view.gridColumns = result.gridColumns;
        if (this.currentGridState !== view) return;
        this.gridStatesService.rememberView(this.gridId, view.id);
        if (notify) this.toastr.success(this.translate.instant('GENERAL.MESSAGES.UPDATED',
          { name: view.name || view.gridId }));
        this.loading = false;
      },
      error: () => { if (this.currentGridState === view) this.loading = false; }
    });
  }

  selectedStoreNames(allStores: any) {
    let names = [];
    this.selectedStores.forEach(storeId => {
      names.push(allStores.find(s => s.id === storeId));
    });

    return names.map(n => n.name).join(', ').slice(0, 18);
  }

  truncate(content: string, chars: number = 30) {
    if (!content) return content;
    if (content.length > chars) {
      return content.slice(0, chars) + '...';
    }
    else {
      return content;
    }
  }

  loadListForAdvancedFilter(field: string, list: any[], prop: string = 'name') {
    let sortedNames = list.map(s => s[prop]).sort((a, b) => a.localeCompare(b));
    sortedNames = sortedNames.filter((n, ix) => sortedNames.indexOf(n) === ix);
    const first = sortedNames[0];
    const idx = this.filterOptions.findIndex(a => a.field === field);
    this.filterOptions[idx].inputType = JSON.stringify(sortedNames);
    this.filterOptions[idx].value = first;
  }

  loadSupplierOptionsForAdvancedFilter() {
    this.suppliersService.getAllSuppliers().pipe(take(1)).subscribe((res: SupplierDto[]) => {
      const sortedSupplierNames = res.map(s => s.name).sort((a, b) => a.localeCompare(b));
      const first = sortedSupplierNames[0];
      const idx = this.filterOptions.findIndex(a => a.title === 'Supplier');
      if (idx !== -1) {
        this.filterOptions[idx].inputType = JSON.stringify(sortedSupplierNames);
        this.filterOptions[idx].value = first;
      }
    });
  }

  loadData() { }
}
